import { FC, useMemo, useState, useRef, useEffect } from 'react'
import { DateTime } from 'luxon'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons'
import { listAllTimeZones } from '@utils/listAllTimeZones'
import { Title } from './Title'
import { twJoin } from 'tailwind-merge'
import { Input } from 'components/form/input'
import { useTranslation } from 'react-i18next'

export interface TimeZoneSelectorProps {
  timeZone: string
  dateEditing: DateTime
  onTimeZoneChange: (timeZone: string) => void
  onBack: () => void
}

export const TimeZoneSelector: FC<TimeZoneSelectorProps> = ({
  timeZone,
  dateEditing,
  onTimeZoneChange,
  onBack,
}) => {
  const { t } = useTranslation()
  const timeZones = useMemo(() => listAllTimeZones(dateEditing), [dateEditing])
  const [filter, setFilter] = useState('')
  const filteredTimeZones = useMemo(() => {
    return timeZones.filter((tz) =>
      tz.label.toLowerCase().includes(filter.toLowerCase()),
    )
  }, [filter, timeZones])
  const selectedZoneRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (selectedZoneRef.current) {
      selectedZoneRef.current.scrollIntoView({
        block: 'center',
      })
    }
  }, [])

  return (
    <div className="flex flex-col gap-3">
      <button
        className="flex items-center gap-2 text-sm font-medium text-neutral-600 ml-4"
        onClick={onBack}
      >
        <FontAwesomeIcon icon={faChevronLeft} />
        {t('common.back')}
      </button>
      <Title title={t('components.timeWidget.updateTimeCardTimeZone')} />
      <div className="mx-3">
        <Input
          autoFocus
          className="h-8"
          placeholder={t('components.timeWidget.filterTimeZones')}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </div>
      <div className="flex flex-col space-y-2 overflow-y-auto max-h-[250px] px-3">
        {filteredTimeZones.map((tz) => (
          <button
            key={tz.name}
            className={twJoin(
              'block w-full text-left px-3 py-2 text-sm',
              tz.name === timeZone
                ? 'bg-primary-600 text-white font-medium'
                : 'text-neutral-700 hover:bg-neutral-100',
            )}
            onClick={() => onTimeZoneChange(tz.name)}
            ref={tz.name === timeZone ? selectedZoneRef : null}
          >
            {tz.label}
          </button>
        ))}
      </div>
    </div>
  )
}
